import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Garden } from '@/types';
import { gardensService, companiesService, sensorsService, controllersService } from '@/services';
import type { ApiZone, ApiController, ApiSensor, ApiCompany } from '@/types/api';
import {
  MapPin,
  Building2,
  Cpu,
  Radio,
  Calendar,
  Zap,
  Ruler,
  Clock,
  Thermometer,
  Droplets,
  Layers,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Play,
  Square,
  History,
  Activity,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface GardenDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garden: Garden | null;
}

export function GardenDetailDialog({
  open,
  onOpenChange,
  garden,
}: GardenDetailDialogProps) {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  
  // Query para obtener zonas del jardín
  // ACTUALIZADO: Backend ahora devuelve formato paginado { items: [], pagination: {} }
  const { data: zonesData, isLoading: isLoadingZones } = useQuery({
    queryKey: ['garden-zones', garden?.id],
    queryFn: async () => {
      if (!garden?.id) return [];
      const response = await gardensService.getZones(garden.id);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar zonas');
      }
      // Extraer items del formato paginado
      return response.data?.items || [];
    },
    enabled: open && !!garden?.id,
  });

  // Query para obtener empresa
  const { data: companyData } = useQuery({
    queryKey: ['company', garden?.companyId],
    queryFn: async () => {
      if (!garden?.companyId) return null;
      const response = await companiesService.getById(garden.companyId);
      if (!response.success) return null;
      return response.data;
    },
    enabled: open && !!garden?.companyId,
  });

  // CAMBIO v0.2.0: Controllers y Sensors pertenecen a Companies, no a Gardens
  // Obtener controladores de la empresa del jardín
  const { data: controllersData, isLoading: isLoadingControllers } = useQuery({
    queryKey: ['garden-controllers', garden?.companyId],
    queryFn: async () => {
      if (!garden?.companyId) return [];
      const response = await controllersService.getAll({ 
        pageSize: 100,
        companyId: garden.companyId 
      });
      if (!response.success) return [];
      return response.data?.items || [];
    },
    enabled: open && !!garden?.companyId,
  });

  // Obtener sensores de la empresa del jardín
  const { data: sensorsData, isLoading: isLoadingSensors } = useQuery({
    queryKey: ['garden-sensors', garden?.companyId],
    queryFn: async () => {
      if (!garden?.companyId) return [];
      const response = await sensorsService.getAll({ 
        pageSize: 100,
        companyId: garden.companyId 
      });
      if (!response.success) return [];
      return response.data?.items || [];
    },
    enabled: open && !!garden?.companyId,
  });

  if (!garden) return null;

  const zones = zonesData || [];
  const controllers = controllersData || [];
  const sensors = sensorsData || [];
  const company = companyData;

  const isLoading = isLoadingZones || isLoadingControllers || isLoadingSensors;

  const onlineControllers = controllers.filter(c => c.status === 'online').length;
  const onlineSensors = sensors.filter(s => s.status === 'online').length;

  // Helpers para buscar por ID
  const getControllerById = (controllerId: string): ApiController | undefined => {
    return controllers.find(c => c.id === controllerId);
  };

  const getSensorById = (sensorId: string): ApiSensor | undefined => {
    return sensors.find(s => s.id === sensorId);
  };

  // Contar total de asignaciones de válvulas
  const totalValveAssignments = zones.reduce(
    (acc, zone) => acc + (zone.controllerAssignments?.length || 0),
    0
  );

  // Obtener estado actual de una válvula
  const getValveState = (controllerId: string, channel: number): 'open' | 'closed' | 'unknown' => {
    const controller = getControllerById(controllerId);
    if (!controller || controller.status === 'offline') return 'unknown';
    const valve = controller.valves?.find(v => v.channel === channel);
    return valve?.status || 'unknown';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{garden.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {garden.municipality}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" className="gap-2">
              <Activity className="w-4 h-4" />
              Estado
            </TabsTrigger>
            <TabsTrigger value="zones" className="gap-2">
              <Layers className="w-4 h-4" />
              Zonas
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              Historial
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 pr-4 mt-4">
            <TabsContent value="overview" className="mt-0 space-y-4">
              {/* Description */}
              {garden.description && (
                <p className="text-sm text-muted-foreground">{garden.description}</p>
              )}

              {/* Company & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    Empresa
                  </div>
                  <p className="font-medium">{company?.name || 'Cargando...'}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Ruler className="w-4 h-4" />
                    Área
                  </div>
                  <p className="font-medium">
                    {garden.area ? `${garden.area.toLocaleString()} m²` : 'No especificada'}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Stats Grid */}
              {isLoading ? (
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className="h-24" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-5 h-5 text-primary" />
                      <span className="font-medium">Zonas</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">{zones.length}</span>
                      <Badge className="bg-success/10 text-success border-0">
                        {totalValveAssignments} válvulas
                      </Badge>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu className="w-5 h-5 text-success" />
                      <span className="font-medium">Controladores</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">{controllers.length}</span>
                      <Badge className="bg-success/10 text-success border-0">
                        {onlineControllers} online
                      </Badge>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Radio className="w-5 h-5 text-info" />
                      <span className="font-medium">Sensores</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">{sensors.length}</span>
                      <Badge className="bg-success/10 text-success border-0">
                        {onlineSensors} online
                      </Badge>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-warning" />
                      <span className="font-medium">Coordenadas</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p>Lat: {garden.coordinates?.lat?.toFixed(6) || '-'}</p>
                      <p>Lng: {garden.coordinates?.lng?.toFixed(6) || '-'}</p>
                    </div>
                  </Card>
                </div>
              )}

              {/* Quick state overview per zone */}
              {zones.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" />
                      Estado Actual por Zona
                    </h4>
                    <div className="space-y-2">
                      {zones.map((zone) => {
                        const activeValves = zone.controllerAssignments?.filter(ca => {
                          const state = getValveState(ca.controllerId, ca.valveChannel);
                          return state === 'open';
                        }) || [];
                        
                        return (
                          <Card key={zone.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{zone.name}</span>
                                {zone.sensorId && (
                                  <Badge variant="outline" className="text-xs gap-1">
                                    <Thermometer className="w-3 h-3" />
                                    Sensor
                                  </Badge>
                                )}
                              </div>
                              {activeValves.length > 0 ? (
                                <Badge className="bg-success/10 text-success border-0 gap-1">
                                  <Play className="w-3 h-3" />
                                  {activeValves.length} válvula(s) activa(s)
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground gap-1">
                                  <Square className="w-3 h-3" />
                                  Sin riego activo
                                </Badge>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="zones" className="mt-0 space-y-4">
              {isLoadingZones ? (
                <div className="space-y-4">
                  {[1, 2].map(i => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
              ) : zones.length === 0 ? (
                <div className="text-center py-8">
                  <Layers className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No hay zonas configuradas</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Edita el jardín para añadir zonas de riego
                  </p>
                </div>
              ) : (
                zones.map((zone) => {
                  const zoneSensor = zone.sensorId ? getSensorById(zone.sensorId) : null;
                  
                  return (
                    <Card key={zone.id} className="overflow-hidden">
                      <div className="p-4 bg-muted/30 border-b">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-primary" />
                            <span className="font-semibold">{zone.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {zoneSensor && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Thermometer className="w-3 h-3" />
                                {zoneSensor.name}
                              </Badge>
                            )}
                            <Badge className="bg-muted text-muted-foreground border-0 text-xs">
                              {zone.controllerAssignments?.length || 0} válvula(s)
                            </Badge>
                          </div>
                        </div>
                        {zone.description && (
                          <p className="text-sm text-muted-foreground mt-2">{zone.description}</p>
                        )}
                      </div>
                      <CardContent className="p-4 space-y-3">
                        {!zone.controllerAssignments || !Array.isArray(zone.controllerAssignments) || zone.controllerAssignments.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">
                            Sin controladores asignados
                          </p>
                        ) : (
                          (zone.controllerAssignments || []).map((assignment) => {
                            const controller = getControllerById(assignment.controllerId);
                            const valveState = getValveState(assignment.controllerId, assignment.valveChannel);

                            return (
                              <div
                                key={assignment.id}
                                className="p-3 rounded-lg border bg-card space-y-2"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Cpu className="w-4 h-4 text-success" />
                                    <span className="font-medium text-sm">
                                      {controller?.name || 'Controlador desconocido'}
                                    </span>
                                    <Badge variant="secondary" className="text-xs">
                                      Válvula {assignment.valveChannel}
                                    </Badge>
                                    {controller?.status === 'offline' && (
                                      <Badge variant="destructive" className="text-xs">
                                        Offline
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Estado actual de la válvula */}
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'text-xs',
                                      valveState === 'open' 
                                        ? 'bg-success/10 text-success border-success/20' 
                                        : valveState === 'closed'
                                          ? 'bg-muted text-muted-foreground'
                                          : 'bg-warning/10 text-warning border-warning/20'
                                    )}
                                  >
                                    {valveState === 'open' && (
                                      <>
                                        <Play className="w-2.5 h-2.5 mr-1" />
                                        Abierta
                                      </>
                                    )}
                                    {valveState === 'closed' && (
                                      <>
                                        <Square className="w-2.5 h-2.5 mr-1" />
                                        Cerrada
                                      </>
                                    )}
                                    {valveState === 'unknown' && (
                                      <>
                                        <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                        Desconocido
                                      </>
                                    )}
                                  </Badge>
                                </div>

                                {/* Controller info */}
                                {controller && (
                                  <div className="pl-6 text-xs text-muted-foreground">
                                    <span>{controller.model}</span>
                                    <span className="mx-2">·</span>
                                    <span>{controller.deviceEUI}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}

                        {/* Sensor info a nivel de zona */}
                        {zoneSensor && (
                          <div className="p-3 rounded-lg bg-info/5 border border-info/20">
                            <div className="flex items-center gap-2 mb-2">
                              <Radio className="w-4 h-4 text-info" />
                              <span className="font-medium text-sm">Sensor: {zoneSensor.name}</span>
                              {zoneSensor.status === 'online' ? (
                                <Badge className="bg-success/10 text-success border-0 text-xs">Online</Badge>
                              ) : (
                                <Badge variant="destructive" className="text-xs">Offline</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>{zoneSensor.model}</span>
                              <span>·</span>
                              <span>{zoneSensor.deviceEUI}</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-0 space-y-4">
              {/* Zone filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Filtrar por zona:</span>
                <Badge
                  variant={selectedZone === null ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedZone(null)}
                >
                  Todas
                </Badge>
                {zones.map(zone => (
                  <Badge
                    key={zone.id}
                    variant={selectedZone === zone.id ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setSelectedZone(zone.id)}
                  >
                    {zone.name}
                  </Badge>
                ))}
              </div>

              {/* TODO: Implementar carga de historial desde API */}
              <div className="text-center py-8">
                <History className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  Historial de eventos
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Próximamente: integración con endpoint de historial
                </p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Timestamps */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            Creado: {format(garden.createdAt, "d 'de' MMMM, yyyy", { locale: es })}
          </div>
          <div>
            Actualizado:{' '}
            {format(garden.updatedAt, "d 'de' MMMM, yyyy", { locale: es })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
